import { atlassianApiGateway } from '@trello/config';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { trelloFetch } from '@trello/fetch';
import { parseNetworkError } from '@trello/graphql-error-handling';
import type { ResolverContext } from './types';
import { getNetworkClient } from './getNetworkClient';

interface AuthDirectory {
  directoryId: string;
  name: string;
  isDefault: boolean;
  // always true unless the directory is scheduled for deletion
  isActive: boolean;
  idpType?: string;
  samlConfigurationId?: string;
  externalDirectoryId?: string;
  domains?: string[];
}

interface AuthDirectoryListResponse {
  directories: AuthDirectory[];
}

interface SamlResponse {
  directoryId: string;
  samlConfigurationId: string;
  samlConfiguration: {
    issuer: string;
    ssoUrl: string;
    publicCertificate: string;
    entityId: string;
    acsUrl: string;
  };
}

export const AvsApiBaseUrl = `${atlassianApiGateway}gateway/api/admin/private`;

async function _fetch(apiUrl: string, context: ResolverContext) {
  return trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'Member.atlassianOrganizations',
      operationName: context.operationName,
    },
  });
}

export async function atlassianOrganizationFlexAuthEnabled(
  orgId: string,
  context: ResolverContext,
): Promise<boolean> {
  // https://developer.atlassian.com/cloud/account-valet/rest/api-group-check-flex-auth-for-an-organization/#api-org-orgid-check-flex-auth-get
  const apiUrl = `${AvsApiBaseUrl}/org/${orgId}/check-flex-auth`;

  const response = await _fetch(apiUrl, context);
  if (response.ok) {
    const { enabled } = await response.json();
    return enabled;
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
      ownershipArea: 'trello-enterprise',
    });
    throw await parseNetworkError(response);
  }
}

export async function atlassianOrganizationSsoUrl(
  idMember: string,
  orgId: string,
  context: ResolverContext,
) {
  // Check Org Flex-Auth status
  const isFlexAuthEnabled = await atlassianOrganizationFlexAuthEnabled(
    orgId,
    context,
  );
  // Fetch org SAML config via AVS or APS
  let apiUrl;
  if (isFlexAuthEnabled) {
    // https://developer.atlassian.com/cloud/account-valet/rest/api-group-get-saml-configuration-for-an-organization/#api-org-orgid-saml-configuration-get
    apiUrl = `${AvsApiBaseUrl}/org/${orgId}/saml-configuration`;
  } else {
    /*
      APS doesn't accept UCT's and there's additional plumbing we would
      have to implement in order to make it accessible from web.

      Instead, we're piggybacking off of Server's integration with APS as
      the most straightforward solution.
    */
    const networkClient = getNetworkClient();
    apiUrl = networkClient.getUrl(
      `/1/members/${idMember}/atlassianOrganizationSsoUrl?atlOrgId=${orgId}`,
    );
  }

  let response = await _fetch(apiUrl, context);

  if (response.ok) {
    const { samlConfiguration } = (await response.json()) as SamlResponse;

    /*
      If status code 200 is returned, but the ssoUrl is empty,
      throw an error, so the component knows to show an error flag.
    */
    if (!samlConfiguration?.ssoUrl) {
      throw new Error(
        `An error occurred while fetching Org SAML Config. ` +
          `(status: ${response.status}, statusText: ${response.statusText}, ` +
          `orgId: ${orgId}, message: SSO Url was empty.`,
      );
    }

    return [samlConfiguration?.ssoUrl];
  } else {
    if (isFlexAuthEnabled && response.status >= 400 && response.status < 500) {
      const { code } = await response.json();
      if (code === 'ORG_ENABLED_FOR_MIDP') {
        // https://developer.atlassian.com/cloud/account-valet/rest/api-group-get-a-list-of-directories-for-an-organization/#api-org-orgid-directories-get
        apiUrl = `${AvsApiBaseUrl}/org/${orgId}/directories`;
        response = await _fetch(apiUrl, context);
        let {
          directories,
        } = (await response.json()) as AuthDirectoryListResponse;
        directories = directories.filter(
          (dir) => !!dir.isActive && !!dir.samlConfigurationId,
        );
        /*
          If there's no valid directory, then this org has no valid
          SSO configuration available, which is not necessarily an error.
        */
        if (!directories.length) {
          return [];
        }

        // https://developer.atlassian.com/cloud/account-valet/rest/api-group-get-saml-configuration-for-a-directory/#api-org-orgid-directory-directoryid-saml-get
        let ssoUrls = await Promise.all(
          directories.map(async (dir) => {
            apiUrl = `${AvsApiBaseUrl}/org/${orgId}/directory/${dir.directoryId}/saml`;
            response = await _fetch(apiUrl, context);
            const {
              samlConfiguration,
            } = (await response.json()) as SamlResponse;
            return Promise.resolve(samlConfiguration?.ssoUrl);
          }),
        );

        /*
          If status code 200 is returned, but there are no valid ssoUrls,
          throw an error, so the component knows to show an error flag.
        */
        ssoUrls = ssoUrls.filter((ssoUrl) => !!ssoUrl);
        if (!ssoUrls.length) {
          throw new Error(
            `An error occurred while fetching Org SAML Config. ` +
              `(status: 200, statusText: ${''}, ` +
              `orgId: ${orgId}, message: SSO Url was empty.`,
          );
        }

        return ssoUrls;
      }
    }

    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
      ownershipArea: 'trello-enterprise',
    });
    throw await parseNetworkError(response);
  }
}
